import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

function AssignmentHistory() {
  const { accessToken } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    pmId: '',
    startDate: '',
    endDate: '',
    limit: 50
  });
  const [pms, setPMs] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    fetchPMs();
    fetchHistory();
  }, []);

  const fetchPMs = async () => {
    try {
      const response = await fetch(`${API_URL}/training/pms`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setPMs(data.data);
      }
    } catch (err) {
      console.error('Error fetching PMs:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (filters.pmId) queryParams.append('pmId', filters.pmId);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const response = await fetch(`${API_URL}/training/assignment-history?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setHistory(data.data);
      } else {
        setError(data.error || 'Failed to fetch assignment history');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Error fetching assignment history:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    fetchHistory();
  };

  const handleClearFilters = () => {
    setFilters({
      pmId: '',
      startDate: '',
      endDate: '',
      limit: 50
    });
    setTimeout(fetchHistory, 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'module_assigned':
        return { icon: '📋', color: 'text-blue-600', bg: 'bg-blue-100' };
      case 'module_unassigned':
        return { icon: '❌', color: 'text-red-600', bg: 'bg-red-100' };
      case 'module_completed':
        return { icon: '✅', color: 'text-green-600', bg: 'bg-green-100' };
      case 'assignment_modified':
        return { icon: '✏️', color: 'text-yellow-600', bg: 'bg-yellow-100' };
      default:
        return { icon: '📝', color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'module_assigned':
        return 'Assigned';
      case 'module_unassigned':
        return 'Unassigned';
      case 'module_completed':
        return 'Completed';
      case 'assignment_modified':
        return 'Modified';
      default:
        return actionType;
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center">
          <span className="mr-3">📜</span>
          Assignment History
        </h1>
        <p className="text-gray-600 mt-2">
          Track all module assignment changes and completions
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <form onSubmit={handleApplyFilters} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* PM Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Manager
              </label>
              <select
                name="pmId"
                value={filters.pmId}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All PMs</option>
                {pms.map(pm => (
                  <option key={pm.id} value={pm.id}>{pm.name}</option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Limit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Show Last
              </label>
              <select
                name="limit"
                value={filters.limit}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="25">25 entries</option>
                <option value="50">50 entries</option>
                <option value="100">100 entries</option>
                <option value="250">250 entries</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* History List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading assignment history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <span className="text-4xl mb-4 block">📭</span>
            <p>No assignment history found</p>
            <p className="text-sm mt-2">Assignments will appear here once modules are assigned to PMs.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date/Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PM
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Module
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {history.map((entry) => {
                    const actionStyle = getActionIcon(entry.action_type);
                    return (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(entry.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionStyle.bg} ${actionStyle.color}`}>
                            <span className="mr-1">{actionStyle.icon}</span>
                            {getActionLabel(entry.action_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {entry.pm_name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {entry.pm_email || ''}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-md">
                          <div className="truncate" title={entry.description}>
                            {entry.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.metadata?.moduleCode && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-mono text-xs">
                              {entry.metadata.moduleCode}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {history.map((entry) => {
                const actionStyle = getActionIcon(entry.action_type);
                return (
                  <div key={entry.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionStyle.bg} ${actionStyle.color}`}>
                        <span className="mr-1">{actionStyle.icon}</span>
                        {getActionLabel(entry.action_type)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(entry.created_at)}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {entry.pm_name || 'Unknown PM'}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {entry.description}
                    </p>
                    {entry.metadata?.moduleCode && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-mono text-xs">
                        {entry.metadata.moduleCode}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Summary */}
      {!loading && history.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          Showing {history.length} entries
        </div>
      )}
    </div>
  );
}

export default AssignmentHistory;
